import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Users, Search, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface MemberInfo {
  username: string;
  servers: Array<{
    id: string;
    name: string;
  }>;
  blacklistedServers: Array<{
    name: string;
    reason: string;
  }>;
}

export function MemberExplorerPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const memberExplorerPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'member-explorer');
  const isEnabled = memberExplorerPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'member-explorer') : pluginApi.disablePlugin(serverId, 'member-explorer'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [memberInfo, setMemberInfo] = useState<MemberInfo | null>(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a username or user ID');
      return;
    }

    setSearching(true);
    try {
      // This would typically call a backend endpoint
      // For now, show a guide on using the commands
      toast.info('Use Discord commands to search for members: /member-info @username');
      setMemberInfo(null);
    } catch (error) {
      toast.error('Error searching for member');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      {/* Header */}
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-500/20 rounded-xl">
            <Users className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Member Explorer</h3>
            <p className="text-sm text-muted-foreground">
              Explore member relationships and server memberships
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {!isEnabled && (
        <div className="glass-card p-4 border border-amber-500/40 bg-amber-500/10 text-sm text-amber-300">
          Plugin disattivato: attivalo con lo switch per usare Member Explorer.
        </div>
      )}

      {/* Info Box */}
      {isEnabled && (
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-400" />
            How to Use Member Explorer
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-sm mb-1">📊 View Member Info</p>
              <p className="text-sm text-muted-foreground">
                <code className="bg-black/30 px-2 py-1 rounded">/member-info @username</code>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Shows all servers the member belongs to
              </p>
            </div>

            <div>
              <p className="font-semibold text-sm mb-1">⚠️ Check Blacklist Status</p>
              <p className="text-sm text-muted-foreground">
                <code className="bg-black/30 px-2 py-1 rounded">/blacklist-check @username</code>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Verify if member is in blacklisted servers
              </p>
            </div>

            <div>
              <p className="font-semibold text-sm mb-1">📋 List Server Members</p>
              <p className="text-sm text-muted-foreground">
                <code className="bg-black/30 px-2 py-1 rounded">/server-members [limit]</code>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                See all members and their total server count
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Search Interface */}
      {isEnabled && (
      <Card>
        <CardHeader>
          <CardTitle>Quick Member Lookup</CardTitle>
          <CardDescription>Search for member information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="member-search">Member Username or ID</Label>
            <div className="flex gap-2">
              <Input
                id="member-search"
                placeholder="username or @mention"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} disabled={searching} size="sm">
                {searching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              ℹ️ Commands work in Discord chat - use /member-info for direct lookup
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Usage Guide */}
      {isEnabled && (
      <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Command Reference</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            {/* Command 1 */}
            <div className="border-l-4 border-blue-500 pl-4 py-2">
              <p className="font-semibold text-sm mb-1">/member-info @username</p>
              <p className="text-sm text-muted-foreground mb-2">
                Displays all servers a member belongs to with membership count
              </p>
              <div className="bg-black/20 p-3 rounded text-xs text-muted-foreground">
                <p className="font-mono">
                  📊 Member Info: john_doe<br />
                  ✅ Community Server<br />
                  ✅ Gaming Hub<br />
                  ✅ Study Group<br />
                  Total: 3 servers
                </p>
              </div>
            </div>

            {/* Command 2 */}
            <div className="border-l-4 border-green-500 pl-4 py-2">
              <p className="font-semibold text-sm mb-1">/blacklist-check @username</p>
              <p className="text-sm text-muted-foreground mb-2">
                Checks if a member is in any blacklisted servers
              </p>
              <div className="bg-black/20 p-3 rounded text-xs text-muted-foreground">
                <p className="font-mono">
                  ✓ SAFE - john_doe is not in any blacklisted servers
                </p>
              </div>
            </div>

            {/* Command 3 */}
            <div className="border-l-4 border-purple-500 pl-4 py-2">
              <p className="font-semibold text-sm mb-1">/server-members [limit]</p>
              <p className="text-sm text-muted-foreground mb-2">
                Lists members of current server with their total server count
              </p>
              <div className="bg-black/20 p-3 rounded text-xs text-muted-foreground">
                <p className="font-mono">
                  📋 Server Members<br />
                  @admin - Member of 5 servers<br />
                  @moderator - Member of 3 servers<br />
                  @john_doe - Member of 3 servers
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            Plugin Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Member Explorer is <span className="font-semibold text-green-400">enabled</span> and ready to use.
            All commands are available in Discord.
          </p>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">💡 Tips</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• Use @mentions or type usernames for member lookup</p>
          <p>• The member must be part of your Discord server to query</p>
          <p>• Server membership data is cached and updated in real-time</p>
          <p>• Combine with Blacklist commands to maintain server safety</p>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
